import trimUrl from "../helpers/trimUrl";
import { EventEmitter } from "events";
import { EndpointModel, EngagementConfiguration } from "../types";
import EndpointService from "../services/EndpointService";
import EngagementConfigService from "../services/EngagementConfigService";
import StoreContainer from "./StoreContainer";

class SettingStore {
  private _emitter = new EventEmitter();
  private _siteId: number;
  private _customVars: any;
  private _clickToCall: boolean;
  private _disableInline: boolean;
  private _disableLauncher: boolean;
  private _disableVisitorMonitoring: boolean;
  private _endpoints: EndpointModel;
  private _config: EngagementConfiguration;
  private _cookieDomain: string;
  private _skipPrechat: boolean;
  private _isLGCanada: boolean;
  private _disableCookies: boolean;

  onConfigChanged(callback: () => void) {
    this._emitter.addListener("configChanged", callback);
  }

  getSiteId() {
    if (this._siteId === undefined) {
      throw new Error("siteId has not been set");
    }

    return this._siteId;
  }

  getSiteIdOrDefault() {
    return this._siteId;
  }

  getClickToCall() {
    if (this._clickToCall === undefined) {
      throw new Error("clickToCall has not been set");
    }

    return this._clickToCall;
  }

  isClickToCall() {
    return this._clickToCall;
  }

  getCustomVars() {
    return this._customVars;
  }

  getShowReferral() {
    return this._config.ShowReferral;
  }

  getShowReferralInFrame() {
    return this._config.ShowReferralInFrame;
  }

  getShowReferralConditionally() {
    return this._config.ShowReferralConditionally;
  }

  getShowUnreadMessageCountInTitle() {
    return this._config.ShowUnreadMessageCountInTitle;
  }

  getReferralWindowWidth() {
    return this._config.ReferralWindowWidth;
  }

  getCallButtonUrl() {
    return this._config.CallButtonUrl;
  }

  getCallButtonNumber() {
    return this._config.CallButtonNumber;
  }

  getReferralWindowHeight() {
    return this._config.ReferralWindowHeight;
  }

  getLandingWidgetsConfig() {
    return this._config.LandingWidgetsConfig;
  }

  isInlineDisabled() {
    return this._disableInline;
  }

  isLauncherDisabled() {
    return this._disableLauncher;
  }

  isVisitorMonitoringDisabled() {
    return this._disableVisitorMonitoring;
  }

  isVisitorTypeaheadEnabled() {
    return this._config.VisitorTypeaheadEnabled;
  }

  isAdvancedEcommerceEnabled() {
    return this._config.AdvancedEcommerceIntegrations;
  }

  shouldSkipPrechat() {
    return this._skipPrechat;
  }

  useIsLGCanada() {
    return this._isLGCanada;
  }

  areCookiesDisabled() {
    return this._disableCookies;
  }

  areLandingWidgetsEnabled() {
    return (
      this._config.LandingWidgetsConfig &&
      this._config.LandingWidgetsConfig.Enabled
    );
  }

  setSiteId(siteId: number) {
    this._siteId = siteId;
  }

  setCustomVars(newData: any) {
    if (!newData) {
      return;
    }

    const savedData = StoreContainer.LocalStorageStore.get("customData") || {
      ...newData
    };

    this._customVars = { ...savedData, ...newData };

    StoreContainer.LocalStorageStore.set("customData", this._customVars);
  }

  setClickToCall(clickToCall: boolean) {
    this._clickToCall = clickToCall;
  }

  setDisableInline(disableInline: boolean) {
    this._disableInline = disableInline;
  }

  setDisableVisitorMonitoring(disableVisitorMonitoring: boolean) {
    this._disableVisitorMonitoring = disableVisitorMonitoring;
  }

  setDisableLauncher(disableLauncher: boolean) {
    this._disableLauncher = disableLauncher;
  }

  setSkipPrechat(skipPrechat: boolean) {
    this._skipPrechat = skipPrechat;
  }

  setIsLGCanada(isLGCanada: boolean) {
    this._isLGCanada = isLGCanada;
  }

  setDisableCookies(disableCookies: boolean) {
    this._disableCookies = disableCookies;
  }

  getMainApiEndpoint() {
    return trimUrl((window.parent.window as any).Velaro.endpoints.mainApi);
  }

  getCdnEndpoint() {
    return trimUrl((window.parent.window as any).Velaro.endpoints.cdn);
  }

  getImagesEndpoint() {
    return `${this.getCdnEndpoint()}/content/images`;
  }

  getSoundEndpoint() {
    return `${this.getCdnEndpoint()}/content/sounds`;
  }

  getEngagementApiEndpoint() {
    if (!this._endpoints) {
      throw new Error("Settings have not been loaded.");
    }

    return trimUrl(this._endpoints.EngagementApiUrl);
  }

  getVisitorApiEndpoint() {
    if (!this._endpoints) {
      throw new Error("Settings have not been loaded.");
    }

    return trimUrl(this._endpoints.VisitorApiUrl);
  }

  getConsoleEndpoint() {
    if (!this._endpoints) {
      throw new Error("Settings have not been loaded.");
    }

    return trimUrl(this._endpoints.ConsoleUrl);
  }

  getEngagementHubEndpoint() {
    if (!this._endpoints) {
      throw new Error("Settings have not been loaded.");
    }

    return trimUrl(this._endpoints.EngagementSignalRUrl);
  }

  getAzureStorageEndpoint() {
    if (!this._endpoints) {
      throw new Error("Settings have not been loaded.");
    }

    return trimUrl(this._endpoints.AzureStorageEndpoint);
  }

  getConfig() {
    return this._config;
  }

  setCookieDomain(domain: string) {
    this._cookieDomain = domain;
  }

  getCookieDomain() {
    return this._cookieDomain;
  }

  async loadEndpoints(): Promise<any> {
    this._endpoints = await EndpointService.fetch(
      this.getMainApiEndpoint(),
      this._siteId
    );
  }

  async loadConfig(): Promise<any> {
    this._config = await EngagementConfigService.fetch(
      this.getMainApiEndpoint(),
      this._siteId,
      StoreContainer.GroupStore.getActiveOrPageGroupId()
    );

    if (this._config.CallsEnabled === false) {
      this.setClickToCall(false);
    }

    this._emitter.emit("configChanged");
  }

  areLoaded() {
    return !!this._endpoints;
  }
}

export default SettingStore;
